import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Facebook, 
  Instagram,
  Menu, 
  X,
  Star,
  Coffee,
  ChefHat,
  Heart,
  Camera,
  ArrowLeft
} from 'lucide-react';
import './App.css';

// Header Component
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  
  const navItems = [
    { id: 'home', label: 'Home', path: '/' },
    { id: 'about', label: 'About', path: '/about' },
    { id: 'gallery', label: 'Gallery', path: '/gallery' },
    { id: 'contact', label: 'Contact Us', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
      <nav className="bg-white px-6 py-4">
        <div className="flex justify-between items-center max-w-7xl mx-auto">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center overflow-hidden">
  <img
    src="https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923200/13599938_1753304258278004_1719583432956950140_n_oqteul.jpg"
    alt="Cafe Marmalade background of the cafe"
    className="w-full h-full object-cover rounded-lg"
  />
</div>
            <span className="text-2xl font-bold text-gray-800">CAFE MARMALADE</span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.id}
                to={item.path}
                className={`text-gray-700 hover:text-amber-600 transition-colors font-medium ${
                  isActive(item.path) ? 'text-amber-600 border-b-2 border-amber-600' : ''
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
            

          {/* Social Icons & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <a href="https://www.facebook.com/cafemarmalde" target="_blank" rel="noopener noreferrer" 
               className="text-blue-600 hover:text-blue-700 transition-colors">
              <Facebook className="w-6 h-6" />
            </a>
            <a href="https://www.instagram.com/cafemarmalade1/" target="_blank" rel="noopener noreferrer" 
               className="text-pink-600 hover:text-pink-700 transition-colors">
              <Instagram className="w-6 h-6" />
            </a>
            <button
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden mt-4 bg-white border-t"
          >
            <div className="flex flex-col space-y-4 py-4">
              {navItems.map((item) => (
                <Link
                  key={item.id}
                  to={item.path}
                  className="text-gray-700 hover:text-amber-600 transition-colors font-medium text-left px-6"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
              <div className="flex space-x-4 px-6 pt-4">
                <a href="https://www.facebook.com/cafemarmalde" target="_blank" rel="noopener noreferrer" 
                   className="text-blue-600 hover:text-blue-700 transition-colors">
                  <Facebook className="w-6 h-6" />
                </a>
                <a href="https://www.instagram.com/cafemarmalade1/" target="_blank" rel="noopener noreferrer" 
                   className="text-pink-600 hover:text-pink-700 transition-colors">
                  <Instagram className="w-6 h-6" />
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </nav>
    </header>
  );
};

// Helper to update page title and meta description
const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);
  }, [title, description]);
};

// Home Page Component
const HomePage = () => {
  usePageMeta(
    'Cafe Marmalade | Banbridge Coffee Shop',
    'Cafe Marmalade is a warm, inviting coffee shop in the heart of Banbridge. Serving fresh food, great coffee, homemade bakes, and a welcoming atmosphere.'
  );
  const foodItems = [
    {
      title: "BAKED GOODS",
      description: "Freshly baked pastries made daily in house. From Croissants to Scones, Traybakes and Sweet treats. We have a wide variety for everyone.",
      image: "https://images.unsplash.com/photo-1587912001191-0cd4f14fd89e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxwYXN0cmllcyUyMGNyb2lzc2FudHN8ZW58MHx8fHwxNzQ5OTE5NTc3fDA&ixlib=rb-4.1.0&q=85",
      bgColor: "bg-gray-800",
      textColor: "text-white"
    },
    {
      title: "TAKEAWAY ORDERS",
      description: "This latest addition to our menu has already become our customers' favourite. Our Chef's Special takeaway dishes is what you've been craving.",
      image: "https://images.pexels.com/photos/312418/pexels-photo-312418.jpeg",
      bgColor: "bg-amber-600",
      textColor: "text-white"
    },
    {
      title: "FRESH FOOD DAILY",
      description: "Our menu is constantly adapting and changing bring you the most delicious tasting food regularly.",
      image: "https://images.pexels.com/photos/11961854/pexels-photo-11961854.jpeg",
      bgColor: "bg-orange-500",
      textColor: "text-white"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative flex items-center justify-center overflow-hidden" style={{height: '75vh'}}>
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923471/IMG-20211005-WA0004_uj83yy.jpg')`
          }}
        />
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-6">
          <motion.h1 
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6 tracking-wide"
          >
            WELCOME TO CAFE<br />MARMALADE
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl md:text-2xl mb-8 font-light"
          >
            A warm inviting coffee shop in the heart of Banbridge
          </motion.p>
          <motion.div 
  initial={{ opacity: 0, scale: 0.8 }}
  animate={{ opacity: 1, scale: 1 }}
  transition={{ duration: 0.8, delay: 0.6 }}
  className="flex flex-col items-center gap-3 sm:flex-row sm:gap-4 justify-center"
>
  <Link 
    to="/about"
    className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors inline-block"
  >
    Discover Our Story
  </Link>
  <Link 
    to="/gallery"
    className="bg-transparent border-2 border-white hover:bg-white hover:text-gray-900 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-colors inline-block"
  >
    View Gallery
  </Link>
</motion.div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-amber-600">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-white"
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">WHO WE ARE</h2>
              <p className="text-lg mb-6 leading-relaxed">
                A warm inviting coffee shop in the heart of Banbridge. We thrive in making good quality food accompanied with exceptional beverages.
              </p>
              <p className="text-lg leading-relaxed mb-8">
                Pop in and try our freshly made to order menu.
              </p>
              <div className="flex items-center space-x-4">
                <Heart className="text-white w-8 h-8" />
                <span className="text-xl font-semibold">Made with Love Since Day One</span>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923200/46463395_2215962762012149_8215724231917305856_n_dj8qr8.jpg" 
                alt="Fresh coffee cup" 
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Food Categories Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">OUR SPECIALTIES</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover our carefully crafted menu, featuring fresh ingredients and traditional recipes that make every visit special.
            </p>
          </motion.div>

          {/* Food Categories Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {foodItems.map((item, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                viewport={{ once: true }}
                className={`${item.bgColor} ${item.textColor} p-8 rounded-lg relative overflow-hidden group hover:transform hover:scale-105 transition-transform duration-300`}
              >
                <div className="relative z-10">
                  <ChefHat className="w-12 h-12 mb-4" />
                  <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                  <p className="leading-relaxed">{item.description}</p>
                </div>
                <div 
                  className="absolute inset-0 bg-cover bg-center opacity-20 group-hover:opacity-30 transition-opacity"
                  style={{ backgroundImage: `url(${item.image})` }}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">FOLLOW US</h2>
            <p className="text-lg text-gray-600 mb-8">
              Stay connected and see what's happening at Cafe Marmalade
            </p>
            <div className="flex justify-center space-x-6">
              <motion.a 
                href="https://www.facebook.com/cafemarmalde" 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-colors"
              >
                <Facebook className="w-8 h-8" />
              </motion.a>
              <motion.a 
                href="https://www.instagram.com/cafemarmalade1" 
                target="_blank" 
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white p-4 rounded-full shadow-lg transition-all"
              >
                <Instagram className="w-8 h-8" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="text-white w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Opening Hours</h3>
              <p className="text-gray-600">Mon-Sat: 8:00 AM - 5:30 PM</p>
              <p className="text-gray-600">Sunday: Closed</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="text-white w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Location</h3>
              <p className="text-gray-600">Bridge St, Banbridge</p>
              <p className="text-gray-600">BT32 3JS</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="text-white w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-800 mb-2">Contact</h3>
              <p className="text-gray-600">028 4062 3388</p>
              <p className="text-gray-600">cafemarmalade69@gmail.com</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// About Page Component
const AboutPage = () => {
  usePageMeta(
    'About Us | Cafe Marmalade Banbridge',
    'Learn the story behind Cafe Marmalade — a locally-owned coffee shop on Bridge Street, Banbridge, passionate about great food, fresh coffee, and a welcoming community.'
  );
  return (
    <div className="pt-24">
      {/* Page Header */}
      <section className="py-20 bg-amber-600">
        <div className="max-w-7xl mx-auto px-6 text-center text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-6"
          >
            ABOUT US
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl font-light"
          >
            Our story, our passion, our commitment to quality
          </motion.p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-800 mb-6">WHO WE ARE</h2>
<p className="text-lg text-gray-600 mb-6 leading-relaxed">
  Welcome to Café Marmalade, a warm and welcoming spot in the heart of Banbridge. Nestled on Bridge Street, we’re proud to be a locally-owned café known for our cosy atmosphere, delicious food, and friendly service.<br /><br />
  Since opening our doors, we’ve been passionate about serving up hearty breakfasts, homemade lunches, and fresh coffee that keeps our guests coming back.
</p>
<p className="text-lg text-gray-600 mb-8 leading-relaxed">
  Whether you’re after a full Marmalade breakfast, a comforting bowl of soup, or a slice of our famous lemon meringue pie, there’s something here for everyone.<br /><br />
  We’re proud to cater to a variety of dietary needs, offering vegetarian and gluten-free options—and we’re always happy to accommodate where we can.
</p>
<p className="text-lg text-gray-600 mb-8 leading-relaxed">
  Our community is at the heart of everything we do, and as we continue to grow (including our exciting new upstairs expansion), we remain committed to offering the same relaxed charm and quality food.<br /><br />
  Pop in, say hello, and make yourself at home.
</p>

            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923542/IMG-20211005-WA0011_foqvo6.jpg" 
                alt="Cafe Marmalade in banbridge" 
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Roots */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-4xl font-bold text-gray-800 mb-8 text-center"
          >
            OUR ROOTS
          </motion.h2>

         <div className="flex flex-col md:flex-row items-center gap-8">
  {/* Image on the left (or right, swap order if you want right) */}
  <img
    src="https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923201/104334434_2650028391938915_3866832752515613580_o_s8czwd.jpg"
    alt="image of cafe marmalade"
    className="w-64 h-64 object-cover rounded-lg shadow-lg"
  />

  <motion.blockquote
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, delay: 0.2 }}
    viewport={{ once: true }}
    className="bg-white border-l-4 border-amber-600 p-6 rounded-lg shadow-md max-w-3xl"
  >
    <p className="text-xl italic text-gray-700">
      "Owning my own business has been a dream of mine. With the help of my partner that dream has become a reality. Many of you will already know this long established café, which was owned and managed by Bernard and Nelly. Their wonderful hard work and dedication to the business has allowed us to acquire a strong and dynamic café. For that we will always be thankful. Our passion going forward will be our customers, offering unique food dishes, which are prepared using locally sourced ingredients, all washed down with specialised beverages made to your specification."
    </p>
    <footer className="mt-4 text-right font-semibold text-amber-600">
      - Wayne McEvoy, Owner
    </footer>
  </motion.blockquote>
</div>

        
        
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">OUR VALUES</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Everything we do is guided by our commitment to quality, community, and creating memorable experiences.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <ChefHat className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Quality Ingredients</h3>
              <p className="text-gray-600 leading-relaxed">
                We source the finest local ingredients and use traditional methods to ensure every dish meets our high standards.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <Heart className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Made with Love</h3>
              <p className="text-gray-600 leading-relaxed">
                Every cup of coffee and every meal is prepared with passion and attention to detail by our dedicated team.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <Coffee className="w-12 h-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Community Focus</h3>
              <p className="text-gray-600 leading-relaxed">
                We're proud to be part of the Banbridge community, creating a welcoming space for everyone to enjoy.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Gallery Page Component
const GalleryPage = () => {
  usePageMeta(
    'Gallery | Cafe Marmalade Banbridge',
    'Browse photos of the food, drinks, and atmosphere at Cafe Marmalade — Banbridge\'s favourite coffee shop on Bridge Street.'
  );
  const galleryImages = [
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749925561/494009256_1200037432132049_4739806211041686717_n_yjye0u.jpg",
    
    },
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923202/116875648_2687623841512703_7149882550858120777_o_oyg5mk.jpg",
    
    },
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923202/118765648_2717063911902029_5927763062150955667_o_amgwkm.jpg"
    },
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923204/119558456_2728813680727052_5471058378276936109_o_fb6wwi.jpg" },
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923204/120091426_2735351390073281_6253397719822052399_n_etncoi.jpg"
    },
  
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923206/121978247_2756298694645217_4088319405053091197_o_ojpc10.jpg"
    },
  
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923205/120242706_2737962076478879_5710102582681708643_n_l0xzew.jpg" },

        {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749925559/489359487_18375956863191990_4981238971323414476_n_kfyoto.png" },
    {
      src: "https://res.cloudinary.com/dz28p7nbx/image/upload/v1749925563/495038470_1206777268124732_7508706561052491504_n_bstxaq.jpg"
    }
  ];

  return (
    <div className="pt-24">
      {/* Page Header */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 text-center text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-6"
          >
            GALLERY
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl font-light"
          >
            A visual journey through our cafe, food, and atmosphere
          </motion.p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-6">OUR VISUAL STORY</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              From our carefully crafted dishes to our warm, inviting atmosphere, every detail tells the story of Cafe Marmalade.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="relative group overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300"
              >
                <img 
                  src={image.src}
                  alt={image.title}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="text-lg font-bold">{image.title}</h3>
                    <p className="text-sm text-gray-300">{image.category}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-amber-600">
        <div className="max-w-7xl mx-auto px-6 text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-6">VISIT US TODAY</h2>
            <p className="text-xl mb-8 font-light">
              Experience the warmth and flavor that make Cafe Marmalade special
            </p>
            <Link 
              to="/contact"
              className="bg-white text-amber-600 px-8 py-3 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors inline-block"
            >
              Get Directions
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Contact Page Component
const ContactPage = () => {
  usePageMeta(
    'Contact Us | Cafe Marmalade Banbridge',
    'Get in touch with Cafe Marmalade. Find us on Bridge Street, Banbridge BT32 3JS. Call us on 028 4062 3388 or email cafemarmalade69@gmail.com.'
  );
  return (
    <div className="pt-24">
      {/* Page Header */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 text-center text-white">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl font-bold mb-6"
          >
            CONTACT US
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-xl font-light"
          >
            Find us, call us, or visit us - we're here to serve you
          </motion.p>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-gray-800 mb-8">GET IN TOUCH</h2>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center">
                    <MapPin className="text-white w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-gray-800">Address</p>
                    <p className="text-gray-600">Bridge St, Banbridge BT32 3JS</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center">
                    <Mail className="text-white w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-gray-800">Email</p>
                    <p className="text-gray-600">cafemarmalade69@gmail.com</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-amber-600 rounded-full flex items-center justify-center">
                    <Phone className="text-white w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-gray-800">Phone</p>
                    <p className="text-gray-600">028 4062 3388</p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <Facebook className="text-white w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-gray-800">Facebook</p>
                    <a href="https://www.facebook.com/cafemarmalde" target="_blank" rel="noopener noreferrer" 
                       className="text-blue-600 hover:text-blue-700 transition-colors">
                      @CafeMarmalade
                    </a>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                    <Instagram className="text-white w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-semibold text-lg text-gray-800">Instagram</p>
                    <a href="https://www.instagram.com/cafemarmalade1" target="_blank" rel="noopener noreferrer" 
                       className="text-pink-600 hover:text-pink-700 transition-colors">
                      @cafemarmalade
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923542/IMG-20211005-WA0012_b0gown.jpg" 
                alt="Cafe Storefront" 
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Opening Hours */}
      <section className="py-20 bg-amber-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923435/IMG-20211005-WA0000_uaymq2.jpg" 
                alt="Cafe Interior" 
                className="rounded-lg shadow-2xl w-full h-96 object-cover"
              />
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-gray-800"
            >
              <h2 className="text-4xl font-bold mb-8 text-amber-600">OPENING HOURS</h2>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between p-6 bg-white rounded-lg shadow-md">
                  <div className="flex items-center space-x-4">
                    <Clock className="text-amber-600 w-6 h-6" />
                    <span className="font-semibold text-lg">Monday - Friday</span>
                  </div>
                  <span className="text-lg font-medium">8:00 AM - 5:30 PM</span>
                </div>
                
                <div className="flex items-center justify-between p-6 bg-white rounded-lg shadow-md">
                  <div className="flex items-center space-x-4">
                    <Clock className="text-amber-600 w-6 h-6" />
                    <span className="font-semibold text-lg">Saturday</span>
                  </div>
                  <span className="text-lg font-medium">8:00 AM - 5:30 PM</span>
                </div>
                
                <div className="flex items-center justify-between p-6 bg-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <Clock className="text-gray-500 w-6 h-6" />
                    <span className="font-semibold text-lg text-gray-600">Sunday</span>
                  </div>
                  <span className="text-lg text-gray-600 font-medium">Closed</span>
                </div>
              </div>

              <div className="mt-8 p-6 bg-amber-100 rounded-lg">
                <h3 className="text-xl font-bold text-amber-800 mb-2">Visit Us Today!</h3>
                <p className="text-amber-700 leading-relaxed">
                  Whether you're looking for a quick coffee to-go or a leisurely brunch with friends, 
                  we're here to serve you with the best quality food and beverages in Banbridge.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-white mb-6">FIND US</h2>
            <p className="text-lg text-gray-300">
              Located in the heart of Banbridge, we're easy to find and even easier to love
            </p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="bg-gray-800 p-8 rounded-lg"
          >
            <div className="rounded-lg overflow-hidden shadow-lg">
  <iframe
    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2325.4306082256912!2d-6.27249402331761!3d54.34937139920447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4860e152c0a9e983%3A0xa416b3204ddc514a!2sCafe%20Marmalade!5e0!3m2!1sen!2suk!4v1749932407642!5m2!1sen!2suk"
    width="100%"
    height="450"
    style={{ border: 0 }}
    allowFullScreen=""
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
    title="Cafe Marmalade Location"
  ></iframe>
</div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-gray-900 text-white py-12">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center overflow-hidden">
  <img
    src="https://res.cloudinary.com/dz28p7nbx/image/upload/v1749923200/13599938_1753304258278004_1719583432956950140_n_oqteul.jpg"
    alt="Cafe Marmalade background of the cafe"
    className="w-full h-full object-cover rounded-lg"
  />
</div>
              <span className="text-xl font-bold">CAFE MARMALADE</span>
            </div>
            <p className="text-gray-300 leading-relaxed mb-4">
              A warm inviting coffee shop in the heart of Banbridge, serving exceptional food and beverages since day one.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/cafemarmalde" target="_blank" rel="noopener noreferrer" 
                 className="text-blue-400 hover:text-blue-300 transition-colors">
                <Facebook className="w-6 h-6" />
              </a>
              <a href="https://www.instagram.com/cafemarmalade1" target="_blank" rel="noopener noreferrer" 
                 className="text-pink-400 hover:text-pink-300 transition-colors">
                <Instagram className="w-6 h-6" />
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Contact Info</h3>
            <div className="space-y-2 text-gray-300">
              <p>Bridge St, Banbridge BT32 3JS</p>
              <p>Phone: 028 4062 3388</p>
              <p>Email: cafemarmalade69@gmail.com</p>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Opening Hours</h3>
            <div className="space-y-2 text-gray-300">
              <p>Monday - Saturday: 8:00 AM - 5:30 PM</p>
              <p>Sunday: Closed</p>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-700 mt-8 pt-8 text-gray-400 flex justify-between items-center">
          <p>&copy; 2026 Cafe Marmalade. All rights reserved.</p>
          <a href="https://ryanphillipsweb.com/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors text-sm">Designed by RPweb</a>
        </div>
      </div>
    </footer>
  );
};

// Main App Component
function App() {

  return (
    <Router>
      <div className="App">
        <Header />
        <main>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
